import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { EventPayloads, KnownEventName } from "@/lib/analyticsEvents";

const getVisitorId = (): string => {
  const key = "visitor_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
};

/** Cached admin status to avoid repeated RPC calls. */
let adminStatusCache: boolean | null = null;
let adminCheckPromise: Promise<boolean> | null = null;

const checkIsAdmin = (): Promise<boolean> => {
  if (adminStatusCache !== null) return Promise.resolve(adminStatusCache);
  if (adminCheckPromise) return adminCheckPromise;
  adminCheckPromise = (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) { adminStatusCache = false; return false; }
      const { data } = await supabase.rpc("has_role", {
        _user_id: session.user.id,
        _role: "admin",
      });
      adminStatusCache = data === true;
      return adminStatusCache;
    } catch {
      adminStatusCache = false;
      return false;
    }
  })();
  return adminCheckPromise;
};

// Reset cache on auth state change
supabase.auth.onAuthStateChange(() => {
  adminStatusCache = null;
  adminCheckPromise = null;
});

const isLovablePreview = (): boolean => {
  const host = window.location.hostname;
  return (
    host.includes("lovableproject.com") ||
    host.includes("lovable.dev") ||
    host.includes("lovable.app")
  );
};

const isAdminDevice = (): boolean => {
  try { return localStorage.getItem("is_admin_device") === "1"; } catch { return false; }
};

const insertEvent = (eventName: string, eventData?: Record<string, unknown>) => {
  if (isLovablePreview()) return; // Never track from Lovable preview
  if (isAdminDevice()) return; // Never track admin devices

  const visitorId = getVisitorId();
  checkIsAdmin().then((isAdmin) => {
    if (isAdmin) return;
    (supabase.from("analytics_events") as any)
      .insert({
        visitor_id: visitorId,
        event_name: eventName,
        event_data: eventData || {},
        path: window.location.pathname,
      })
      .then(({ error }: { error: any }) => {
        if (error) console.error("Event tracking error:", error);
      });
  });
};

/**
 * Sygnatura overload-owana:
 *  - dla nazw zarejestrowanych w `EventPayloads` wymaga zgodnego payloadu (TS pilnuje pól),
 *  - dla wartości spoza listy akceptuje dowolny `Record<string, unknown>` (escape hatch).
 *
 * Patrz `src/lib/analyticsEvents.ts` po listę pól i konwencje (shelter_id, source, type, ...).
 */
export function trackEvent<K extends KnownEventName>(eventName: K, eventData: EventPayloads[K]): void;
export function trackEvent(eventName: string, eventData?: Record<string, unknown>): void;
export function trackEvent(eventName: string, eventData?: Record<string, unknown>): void {
  insertEvent(eventName, eventData);
}

export const useEventTracking = () => {
  const track = useCallback(
    <K extends KnownEventName>(eventName: K | string, eventData?: EventPayloads[K] | Record<string, unknown>) => {
      insertEvent(eventName, eventData as Record<string, unknown> | undefined);
    },
    [],
  );
  return { trackEvent: track };
};

